import { ReadonlyURLSearchParams } from 'next/navigation'
import { NextRouter } from 'next/router'
import queryString from 'query-string'

import { getProductOptionNamesFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import {
  AddOnProductOptionGroup,
  AddOnProductOptionGroupItem,
} from '@src/quince-site-common/src/domains/AddOnProductOptionGroup'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { getURLStringFromInputString } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

export const findExactOrContainingHandleGroupItem = (
  groupItems: AddOnProductOptionGroup['groupItems'],
  currentPathName: string,
): AddOnProductOptionGroupItem | undefined => {
  const exactHandleMatchItem = groupItems.find(item => {
    const adjustedHandle = item.productHandle.startsWith('/') ? item.productHandle : `/${item.productHandle}`
    return adjustedHandle === currentPathName
  })
  if (exactHandleMatchItem) return exactHandleMatchItem

  return groupItems.find(item => (isBrowser() ? window.location.href.includes(item.productHandle) : false))
}

export const getFullNameFromHyphenatedName = (hypenatedName: string) =>
  hypenatedName.charAt(0).toUpperCase() + hypenatedName.slice(1).replace(/-([a-z])/g, g => ` ${g[1].toUpperCase()}`)

export const getOptionBasedOnQueryString = (
  product: Product,
  optionKey: string,
  optionName: ProductOptionName,
  queryParamString?: string,
): ProductVariantOption | undefined => {
  const query = queryParamString ?? (isBrowser() ? window.location.search : '')
  let option: string | undefined = undefined
  if (query) {
    const queryParams = queryString.parse(query)
    if (queryParams[optionKey]) {
      option = getFullNameFromHyphenatedName(queryParams[optionKey] as string)
    }
    if (option) {
      const optionString = option
      const optionValue = product.productOptions
        .find(productOption => productOption.name == optionName)
        ?.values?.find(value => matchOptionValue(value.value, optionString))
      if (optionValue) {
        return new ProductVariantOption({
          name: optionName,
          value: optionValue.value,
        })
      }
    }
  }
  return undefined
}

export const updateOptionsInUrl = (
  pathname: string,
  searchParams: ReadonlyURLSearchParams,
  selectedOptions: ProductVariantOption[],
  router: NextRouter,
) => {
  const updatedSearchParams = new URLSearchParams(Array.from(searchParams.entries())) // -> has to use this form

  const colorOption = selectedOptions.filter(o => o.name == ProductOptionName.Color)[0]?.value
  const sizeOption = selectedOptions.filter(o => o.name == ProductOptionName.Size)[0]?.value
  const productOptionNames = getProductOptionNamesFromSelectedOptions(selectedOptions)
  if (sizeOption && updatedSearchParams.get('size')) {
    updatedSearchParams.set('size', getURLStringFromInputString(sizeOption))
  }
  if (colorOption) {
    updatedSearchParams.set('color', getURLStringFromInputString(colorOption))
  }
  productOptionNames.forEach(name => {
    const option = selectedOptions.filter(o => o.name == name)[0]?.value
    if (option) {
      updatedSearchParams.set(name.toLowerCase(), getURLStringFromInputString(option))
    }
  })
  updateSearchParams(searchParams, updatedSearchParams, router, pathname)
}

export const updateSearchParams = (
  originalSearchParams: ReadonlyURLSearchParams,
  updatedSearchParams: URLSearchParams,
  router: NextRouter,
  pathname: string,
) => {
  const newSearchString = updatedSearchParams.toString()
  const originalSearchString = originalSearchParams.toString()
  if (newSearchString !== originalSearchString) {
    const query = newSearchString ? `?${newSearchString}` : ''
    void router.replace(`${pathname}${query}`, `${pathname}${query}`, { scroll: false, shallow: false })
  }
}

const matchOptionValue = (value1: string, value2: string) => {
  return value1.toLowerCase().replace(/[^a-z0-9]*/g, '') === value2.toLowerCase().replace(/[^a-z0-9]*/g, '')
}
